/* eslint-disable react-hooks/set-state-in-effect */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Users, Shield, Plus, Edit2, Trash2, CheckCircle,
  XCircle, Clock, Search, ChevronDown, Building2,
  AlertTriangle, RefreshCw, UserCheck, UserX, Info,
  ScrollText, ChevronLeft, ChevronRight,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Role badge colours ────────────────────────────────────────────────────────
// Covers both legacy group_role keys (ms_exec, tf_admin, ms_vendor …) used by the
// group creation form, AND the canonical role_type values returned by the upstream
// portal_groups API (ms_executive, tf_reporting_admin, ms_reporting_vendor …).
const ROLE_META = {
  // Legacy group_role keys (group creation form)
  tf_admin:      { label: 'TF Admin',              color: '#D4EC8E', bg: 'rgba(212,236,142,0.15)', dot: '#22c55e' },
  ms_exec:       { label: 'MS Executive / FTE',    color: '#60a5fa', bg: 'rgba(96,165,250,0.12)',  dot: '#3b82f6' },
  ms_vendor:     { label: 'MS Reporting Vendor',   color: '#a78bfa', bg: 'rgba(167,139,250,0.12)', dot: '#8b5cf6' },
  ms_pdm:        { label: 'MS PDM',                color: '#fb923c', bg: 'rgba(251,146,60,0.12)',  dot: '#f97316' },
  ms_prog_admin: { label: 'Program / Project Admin', color: '#34d399', bg: 'rgba(52,211,153,0.12)', dot: '#10b981' },
  // Canonical role_type values from portal_groups (upstream RBAC API)
  ms_executive:        { label: 'MS Executive / FTE',    color: '#60a5fa', bg: 'rgba(96,165,250,0.12)',  dot: '#3b82f6' },
  ms_reporting_vendor: { label: 'MS Reporting Vendor',   color: '#a78bfa', bg: 'rgba(167,139,250,0.12)', dot: '#8b5cf6' },
  tf_report_viewer:    { label: 'TF Report Viewer',      color: '#D4EC8E', bg: 'rgba(212,236,142,0.15)', dot: '#22c55e' },
  tf_reporting_admin:  { label: 'TF Reporting Admin',    color: '#D4EC8E', bg: 'rgba(212,236,142,0.15)', dot: '#22c55e' },
  unassigned:          { label: 'Unassigned',            color: '#9ca3af', bg: 'rgba(156,163,175,0.1)',  dot: '#6b7280' },
};

const ACTION_META = {
  user_added:       { label: 'User Added',      color: '#10b981', bg: 'rgba(16,185,129,0.12)'  },
  user_approved:    { label: 'User Approved',   color: '#22c55e', bg: 'rgba(34,197,94,0.12)'   },
  user_rejected:    { label: 'User Rejected',   color: '#ef4444', bg: 'rgba(239,68,68,0.12)'   },
  user_deleted:     { label: 'User Deleted',    color: '#dc2626', bg: 'rgba(220,38,38,0.12)'   },
  user_deactivated: { label: 'Deactivated',     color: '#f59e0b', bg: 'rgba(245,158,11,0.12)'  },
  user_reactivated: { label: 'Reactivated',     color: '#3b82f6', bg: 'rgba(59,130,246,0.12)'  },
  user_updated:     { label: 'User Updated',    color: '#6366f1', bg: 'rgba(99,102,241,0.12)'  },
  group_created:    { label: 'Group Created',   color: '#0ea5e9', bg: 'rgba(14,165,233,0.12)'  },
  group_updated:    { label: 'Group Updated',   color: '#8b5cf6', bg: 'rgba(139,92,246,0.12)'  },
  group_deleted:    { label: 'Group Deleted',   color: '#ef4444', bg: 'rgba(239,68,68,0.12)'   },
};

function ActionBadge({ action }) {
  const m = ACTION_META[action] || { label: action, color: '#9ca3af', bg: 'rgba(156,163,175,0.1)' };
  return (
    <span style={{
      background: m.bg, color: m.color,
      fontSize: 11, fontWeight: 700, padding: '3px 9px',
      borderRadius: 20, letterSpacing: '0.02em', whiteSpace: 'nowrap',
    }}>
      {m.label}
    </span>
  );
}

function RoleBadge({ role }) {
  const m = ROLE_META[role] || ROLE_META.unassigned;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      background: m.bg, color: m.color,
      fontSize: 11, fontWeight: 700, padding: '3px 8px',
      borderRadius: 20, letterSpacing: '0.03em',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: m.dot }} />
      {m.label}
    </span>
  );
}

function StatusBadge({ status }) {
  const colours = {
    active:   { bg: 'rgba(52,211,153,0.12)',  color: '#10b981', label: 'Active' },
    pending:  { bg: 'rgba(251,191,36,0.15)',   color: '#f59e0b', label: 'Pending' },
    inactive: { bg: 'rgba(248,113,113,0.12)',  color: '#ef4444', label: 'Inactive' },
  };
  const c = colours[status] || colours.inactive;
  return (
    <span style={{
      background: c.bg, color: c.color, fontSize: 11, fontWeight: 700,
      padding: '2px 8px', borderRadius: 20,
    }}>
      {c.label}
    </span>
  );
}

// ── POID chip (for PDM groups) ─────────────────────────────────────────────────
function PoidChip({ poid, name, onRemove }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      background: 'rgba(251,146,60,0.12)', border: '1px solid rgba(251,146,60,0.3)',
      color: '#fb923c', fontSize: 12, fontWeight: 600,
      padding: '3px 10px', borderRadius: 20,
    }}>
      {name || poid}
      {onRemove && (
        <button onClick={onRemove} style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: '#fb923c', padding: 0, display: 'flex',
          lineHeight: 1,
        }}>×</button>
      )}
    </span>
  );
}

// ── Group Form Dialog ──────────────────────────────────────────────────────────
function GroupDialog({ initial, onSave, onClose }) {
  const [form, setForm] = useState({
    group_name: initial?.group_name || '',
    group_role: initial?.group_role || 'ms_exec',
    description: initial?.description || '',
    partner_one_ids: initial?.partner_one_ids || [],
    program_access: initial?.program_access || { projects: [], pages: [] },
  });
  const [poidInput, setPoidInput] = useState('');
  const [poidLookups, setPoidLookups] = useState({});
  const [poidPreview, setPoidPreview] = useState(null); // null | {loading} | {found, esi_partner_name, total_domain_count, ...}
  const poidDebounceRef = useRef(null);
  const [pages, setPages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/admin/access/pages`).then(r => setPages(r.data.data || [])).catch(() => {});
    // pre-populate POID lookup names for existing PDM groups
    if (initial?.partner_one_ids) {
      initial.partner_one_ids.forEach(poid => {
        axios.get(`${API}/api/admin/access/poid-lookup?poid=${poid}`)
          .then(r => setPoidLookups(prev => ({ ...prev, [poid]: r.data.esi_partner_name })))
          .catch(() => {});
      });
    }
  }, []);

  // Live POID preview — debounced 500ms as admin types
  useEffect(() => {
    if (poidDebounceRef.current) clearTimeout(poidDebounceRef.current);
    const val = poidInput.trim();
    if (!val) { setPoidPreview(null); return; }
    setPoidPreview({ loading: true });
    poidDebounceRef.current = setTimeout(() => {
      axios.get(`${API}/api/admin/access/poid-lookup?poid=${encodeURIComponent(val)}`)
        .then(r => setPoidPreview({ found: true, ...r.data }))
        .catch(() => setPoidPreview({ found: false }));
    }, 500);
    return () => clearTimeout(poidDebounceRef.current);
  }, [poidInput]);

  const lookupPoid = () => {
    if (!poidInput.trim()) return;
    // If live preview already resolved successfully, reuse that data
    if (poidPreview?.found) {
      const name = poidPreview.esi_partner_name;
      setPoidLookups(prev => ({ ...prev, [poidInput.trim()]: name }));
      setForm(f => ({
        ...f,
        partner_one_ids: [...new Set([...f.partner_one_ids, poidInput.trim()])],
      }));
      setPoidInput('');
      setPoidPreview(null);
      return;
    }
    setLookingUp(true);
    axios.get(`${API}/api/admin/access/poid-lookup?poid=${poidInput.trim()}`)
      .then(r => {
        const name = r.data.esi_partner_name;
        setPoidLookups(prev => ({ ...prev, [poidInput.trim()]: name }));
        setForm(f => ({
          ...f,
          partner_one_ids: [...new Set([...f.partner_one_ids, poidInput.trim()])],
        }));
        setPoidInput('');
        setPoidPreview(null);
      })
      .catch(_e => alert(`POID "${poidInput}" not found.`))
      .finally(() => setLookingUp(false));
  };

  const removePoid = (poid) => {
    setForm(f => ({ ...f, partner_one_ids: f.partner_one_ids.filter(p => p !== poid) }));
  };

  const togglePage = (key) => {
    setForm(f => {
      const pages = f.program_access.pages || [];
      const next = pages.includes(key) ? pages.filter(p => p !== key) : [...pages, key];
      return { ...f, program_access: { ...f.program_access, pages: next } };
    });
  };

  const handleSave = () => {
    if (!form.group_name.trim()) { alert('Group name is required.'); return; }
    setSaving(true);
    const payload = { ...form };
    if (form.group_role !== 'ms_pdm') payload.partner_one_ids = [];
    if (form.group_role !== 'ms_prog_admin') payload.program_access = {};
    Promise.resolve(onSave(payload)).finally(() => setSaving(false));
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 2000, padding: 20,
    }}>
      <div style={{
        background: '#fff', borderRadius: 12, width: '100%', maxWidth: 560,
        maxHeight: '90vh', overflow: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        fontFamily: '"DM Sans", sans-serif',
      }}>
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#091F2C' }}>
            {initial ? 'Edit Group' : 'Create Permission Group'}
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: 20 }}>×</button>
        </div>

        <div style={{ padding: 24 }}>
          {/* Group Name */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Group Name *</label>
            <input
              value={form.group_name}
              onChange={e => setForm(f => ({ ...f, group_name: e.target.value }))}
              placeholder="e.g. Insights Cube Team (MAQ)"
              style={inputStyle}
              data-testid="group-name-input"
            />
          </div>

          {/* Role */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Group Role *</label>
            <select
              value={form.group_role}
              onChange={e => setForm(f => ({ ...f, group_role: e.target.value }))}
              style={inputStyle}
              data-testid="group-role-select"
            >
              <option value="ms_exec">MS Executive / FTE / SA Lead — No PII, All Reports</option>
              <option value="ms_vendor">MS Reporting Vendor (PII access) — All Reports + PII</option>
              <option value="ms_pdm">MS PDM — Partner-Filtered Reports, No PII</option>
              <option value="ms_prog_admin">Program / Project Admin — Scoped Reports, No PII</option>
              <option value="tf_admin">TF Admin — Full Access + PII + Admin</option>
            </select>
          </div>

          {/* Description */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Description</label>
            <input
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Optional description or notes"
              style={inputStyle}
            />
          </div>

          {form.group_role === 'ms_pdm' && (
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Managed Partner POIDs</label>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input
                  value={poidInput}
                  onChange={e => setPoidInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && lookupPoid()}
                  placeholder="Enter POID (e.g. 6452033)"
                  style={{ ...inputStyle, flex: 1, marginBottom: 0 }}
                  data-testid="poid-input"
                />
                <button
                  onClick={lookupPoid}
                  disabled={lookingUp || !poidInput.trim() || poidPreview?.loading}
                  style={btnSecondary}
                  data-testid="poid-lookup-btn"
                >
                  {lookingUp ? '…' : poidPreview?.found ? 'Add' : 'Look up'}
                </button>
              </div>

              {/* Live preview card */}
              {poidPreview && (
                <div style={{
                  marginBottom: 10, padding: '10px 14px', borderRadius: 8,
                  background: poidPreview.loading
                    ? '#f9fafb'
                    : poidPreview.found
                      ? 'rgba(251,146,60,0.06)'
                      : 'rgba(239,68,68,0.05)',
                  border: `1.5px solid ${poidPreview.loading ? '#e5e7eb' : poidPreview.found ? 'rgba(251,146,60,0.35)' : 'rgba(239,68,68,0.3)'}`,
                  transition: 'all 0.2s',
                }} data-testid="poid-preview-card">
                  {poidPreview.loading ? (
                    <span style={{ fontSize: 12, color: '#9ca3af' }}>Looking up POID…</span>
                  ) : poidPreview.found ? (
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                        <Building2 size={13} color="#fb923c" />
                        <span style={{ fontWeight: 700, fontSize: 13, color: '#111827' }}>
                          {poidPreview.esi_partner_name}
                        </span>
                        <span style={{ fontSize: 11, color: '#9ca3af' }}>({poidInput.trim()})</span>
                      </div>
                      <div style={{ fontSize: 12, color: '#6b7280', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                        <span>
                          <span style={{ fontWeight: 700, color: '#fb923c' }}>{poidPreview.total_domain_count}</span>
                          {' '}domain{poidPreview.total_domain_count !== 1 ? 's' : ''}
                        </span>
                        {poidPreview.partner_group && <span>Group: <strong>{poidPreview.partner_group}</strong></span>}
                        {poidPreview.type && <span>Type: <strong>{poidPreview.type}</strong></span>}
                      </div>
                      {poidPreview.sample_domains?.length > 0 && (
                        <div style={{ marginTop: 5, fontSize: 11, color: '#9ca3af', lineHeight: 1.5 }}>
                          Sample: {poidPreview.sample_domains.slice(0, 5).join(', ')}{poidPreview.total_domain_count > 5 ? ` +${poidPreview.total_domain_count - 5} more` : ''}
                        </div>
                      )}
                      {form.partner_one_ids.includes(poidInput.trim()) && (
                        <div style={{ marginTop: 5, fontSize: 11, color: '#f97316', fontWeight: 700 }}>
                          Already in this group
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      <AlertTriangle size={13} color="#ef4444" />
                      <span style={{ fontSize: 12, color: '#ef4444', fontWeight: 600 }}>
                        POID not found in database
                      </span>
                    </div>
                  )}
                </div>
              )}

              {form.partner_one_ids.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {form.partner_one_ids.map(poid => (
                    <PoidChip
                      key={poid} poid={poid}
                      name={poidLookups[poid] ? `${poidLookups[poid]} (${poid})` : poid}
                      onRemove={() => removePoid(poid)}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Program Admin: Accessible Report Pages only (no project-level scoping) */}
          {form.group_role === 'ms_prog_admin' && (
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Accessible Report Pages</label>
              <div style={{
                border: '1.5px solid #dde3e5', borderRadius: 6,
                maxHeight: 200, overflow: 'auto', padding: 8,
              }}>
                {pages.map(page => (
                  <label key={page.key} style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '4px 0', cursor: 'pointer', fontSize: 13, color: '#374151',
                  }}>
                    <input
                      type="checkbox"
                      checked={(form.program_access.pages || []).includes(page.key)}
                      onChange={() => togglePage(page.key)}
                    />
                    {page.label}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} style={btnSecondary}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={btnPrimary} data-testid="save-group-btn">
            {saving ? 'Saving…' : (initial ? 'Save Changes' : 'Create Group')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Add User Dialog ────────────────────────────────────────────────────────────
function AddUserDialog({ groups, onSave, onClose }) {
  const [email, setEmail] = useState('');
  const [groupId, setGroupId] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = () => {
    if (!email.trim()) { alert('Email is required.'); return; }
    setSaving(true);
    Promise.resolve(onSave({ email: email.trim(), group_id: groupId ? parseInt(groupId) : null, notes, status: 'active' }))
      .finally(() => setSaving(false));
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 460, boxShadow: '0 20px 60px rgba(0,0,0,0.3)', fontFamily: '"DM Sans", sans-serif' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#091F2C' }}>Add User</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: 20 }}>×</button>
        </div>
        <div style={{ padding: 24 }}>
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Email Address *</label>
            <input value={email} onChange={e => setEmail(e.target.value)} placeholder="user@microsoft.com or user@technofocus.co" style={inputStyle} data-testid="add-user-email-input" />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Assign to Group</label>
            <select value={groupId} onChange={e => setGroupId(e.target.value)} style={inputStyle} data-testid="add-user-group-select">
              <option value="">— No group (will be pending) —</option>
              {groups.filter(g => g.is_active).map(g => (
                <option key={g.id} value={g.id}>{g.group_name} ({ROLE_META[g.group_role]?.label || g.group_role})</option>
              ))}
            </select>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Notes (optional)</label>
            <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Reason or context" style={inputStyle} />
          </div>
        </div>
        <div style={{ padding: '16px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} style={btnSecondary}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={btnPrimary} data-testid="add-user-save-btn">
            {saving ? 'Adding…' : 'Add User'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Edit User Dialog ───────────────────────────────────────────────────────────
function EditUserDialog({ user, groups, onSave, onClose }) {
  const [groupId, setGroupId] = useState(user.group_id ? String(user.group_id) : '');
  const [role, setRole] = useState(user.effective_role || 'ms_exec');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = () => {
    setSaving(true);
    const payload = { notes: notes || undefined };
    if (groupId) {
      payload.group_id = parseInt(groupId);
    } else {
      payload.portal_role = role;
    }
    Promise.resolve(onSave(payload)).finally(() => setSaving(false));
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 460, boxShadow: '0 20px 60px rgba(0,0,0,0.3)', fontFamily: '"DM Sans", sans-serif' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#091F2C' }}>Edit User Role</h2>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>{user.email}</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: 20 }}>×</button>
        </div>
        <div style={{ padding: 24 }}>
          {/* Current role info */}
          <div style={{ marginBottom: 16, padding: '10px 12px', background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 13 }}>
            <span style={{ color: '#6b7280' }}>Current:</span>{' '}
            <RoleBadge role={user.effective_role} />
            {user.group_name && <span style={{ color: '#9ca3af', marginLeft: 8 }}>({user.group_name})</span>}
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Assign to Group (recommended)</label>
            <select value={groupId} onChange={e => setGroupId(e.target.value)} style={inputStyle} data-testid="edit-user-group-select">
              <option value="">— Assign role directly (no group) —</option>
              {groups.filter(g => g.is_active).map(g => (
                <option key={g.id} value={g.id}>{g.group_name} ({ROLE_META[g.group_role]?.label || g.group_role})</option>
              ))}
            </select>
          </div>

          {!groupId && (
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Direct Role</label>
              <select value={role} onChange={e => setRole(e.target.value)} style={inputStyle} data-testid="edit-user-role-select">
                {Object.entries(ROLE_META).filter(([k]) => k !== 'unassigned').map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Notes (optional)</label>
            <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Reason for change" style={inputStyle} />
          </div>
        </div>
        <div style={{ padding: '16px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} style={btnSecondary}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={btnPrimary} data-testid="edit-user-save-btn">
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Approve Dialog ─────────────────────────────────────────────────────────────
function ApproveDialog({ user, groups, onSave, onClose }) {
  const [groupId, setGroupId] = useState('');
  const [role, setRole] = useState('ms_exec');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const handleApprove = () => {
    setSaving(true);
    Promise.resolve(onSave({
      group_id: groupId ? parseInt(groupId) : null,
      portal_role: groupId ? null : role,
      notes,
    })).finally(() => setSaving(false));
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 460, boxShadow: '0 20px 60px rgba(0,0,0,0.3)', fontFamily: '"DM Sans", sans-serif' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#091F2C' }}>Approve Access</h2>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>{user.email}</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: 20 }}>×</button>
        </div>
        <div style={{ padding: 24 }}>
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Assign to Group (recommended)</label>
            <select value={groupId} onChange={e => setGroupId(e.target.value)} style={inputStyle} data-testid="approve-group-select">
              <option value="">— Assign role directly —</option>
              {groups.filter(g => g.is_active).map(g => (
                <option key={g.id} value={g.id}>{g.group_name} ({ROLE_META[g.group_role]?.label || g.group_role})</option>
              ))}
            </select>
          </div>
          {!groupId && (
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Direct Role</label>
              <select value={role} onChange={e => setRole(e.target.value)} style={inputStyle}>
                {Object.entries(ROLE_META).filter(([k]) => k !== 'unassigned').map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>
          )}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Notes (optional)</label>
            <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Approval context" style={inputStyle} />
          </div>
        </div>
        <div style={{ padding: '16px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} style={btnSecondary}>Cancel</button>
          <button onClick={handleApprove} disabled={saving || (!groupId && !role)} style={{ ...btnPrimary, background: '#10b981' }} data-testid="confirm-approve-btn">
            {saving ? 'Approving…' : 'Approve Access'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Custom hook: all state + side effects ─────────────────────────────────────
function useAccessManagement() {
  const [activeTab, setActiveTab] = useState('pending');
  // Ref tracks current tab synchronously so in-flight async callbacks
  // don't surface stale errors after the user has switched tabs (race condition fix).
  const activeTabRef = useRef('pending');

  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [groupDialog, setGroupDialog] = useState(null);
  const [addUserDialog, setAddUserDialog] = useState(false);
  const [approveDialog, setApproveDialog] = useState(null);
  const [editUserDialog, setEditUserDialog] = useState(null);
  const [error, setError] = useState('');
  const [upstreamInfo, setUpstreamInfo] = useState('');
  // groupFilter: { id, name } | null — client-side drill-down from Groups tab
  const [groupFilter, setGroupFilter] = useState(null);

  // Wraps setActiveTab to keep the ref in sync before any async work.
  const switchTab = (tab) => {
    activeTabRef.current = tab;
    setActiveTab(tab);
  };

  // Called from Groups tab "N members" button — switches to Users tab filtered to that group.
  const viewGroupMembers = (group) => {
    setGroupFilter({ id: group.id, name: group.group_name });
    setStatusFilter('all');
    setSearch('');
    switchTab('users');
  };

  const loadGroups = () => {
    const tabAtCall = activeTabRef.current;
    axios.get(`${API}/api/admin/access/groups`)
      .then(r => {
        setGroups(r.data.data || []);
        if (r.data._upstream_info) setUpstreamInfo(r.data._upstream_info);
      })
      .catch(err => {
        const detail = err.response?.data?.detail;
        if (activeTabRef.current === tabAtCall) setError(detail || 'Failed to load groups.');
      });
  };

  const loadPending = () => {
    axios.get(`${API}/api/admin/access/users/pending`)
      .then(r => {
        setPendingUsers(r.data.data || []);
        setPendingCount(r.data.total || 0);
        if (r.data._upstream_info) setUpstreamInfo(r.data._upstream_info);
      })
      .catch(_e => { /* silent */ });
  };

  const loadUsers = () => {
    const tabAtCall = activeTabRef.current;
    setLoading(true);
    const params = { per_page: 200 };  // fetch up to 200 so group drill-down shows all members
    if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
    if (search) params.search = search;
    axios.get(`${API}/api/admin/access/users`, { params })
      .then(r => {
        setUsers(r.data.data || []);
        if (r.data._upstream_info) {
          setUpstreamInfo(r.data._upstream_info);
        } else {
          setUpstreamInfo('');
        }
      })
      .catch(err => {
        const detail = err.response?.data?.detail;
        if (activeTabRef.current === tabAtCall) setError(detail || 'Failed to load users.');
      })
      .finally(() => {
        if (activeTabRef.current === tabAtCall) setLoading(false);
      });
  };

  const handleCreateGroup = (payload) => {
    setGroupDialog(null);
    axios.post(`${API}/api/admin/access/groups`, payload)
      .then(() => loadGroups())
      .catch(e => setError(e?.response?.data?.detail || e?.message || 'Failed to create group.'));
  };

  const handleUpdateGroup = (payload) => {
    const gid = groupDialog?.initial?.id;
    setGroupDialog(null);
    axios.put(`${API}/api/admin/access/groups/${gid}`, payload)
      .then(() => loadGroups())
      .catch(e => setError(e?.response?.data?.detail || e?.message || 'Failed to update group.'));
  };

  const handleDeleteGroup = (id, groupName) => {
    if (!window.confirm(`Delete group "${groupName}"? This cannot be undone.`)) return;
    axios.delete(`${API}/api/admin/access/groups/${id}`, { withCredentials: true })
      .then(() => loadGroups())
      .catch(e => {
        const msg = e?.response?.data?.detail || e?.message || 'Failed to delete group.';
        setError(`Delete failed: ${msg}`);
      });
  };

  const handleAddUser = (payload) => {
    setAddUserDialog(false);
    axios.post(`${API}/api/admin/access/users`, payload)
      .then(() => loadUsers())
      .catch(e => setError(e?.response?.data?.detail || e?.message || 'Failed to add user.'));
  };

  const handleEditUser = (userId, payload) => {
    setEditUserDialog(null);
    axios.put(`${API}/api/admin/access/users/${userId}`, payload)
      .then(() => loadUsers())
      .catch(e => setError(e?.response?.data?.detail || e?.message || 'Failed to update user role.'));
  };

  const handleApprove = (userId, payload) => {
    setApproveDialog(null);
    axios.post(`${API}/api/admin/access/users/${userId}/approve`, payload)
      .then(() => { setPendingCount(c => Math.max(0, c - 1)); loadPending(); })
      .catch(e => setError(e?.response?.data?.detail || e?.message || 'Failed to approve user.'));
  };

  const handleReject = (userId) => {
    if (!window.confirm('Reject this user? They will be marked as inactive.')) return;
    axios.post(`${API}/api/admin/access/users/${userId}/reject`, { notes: 'Rejected by admin' })
      .then(() => { setPendingCount(c => Math.max(0, c - 1)); loadPending(); })
      .catch(e => setError(e?.response?.data?.detail || e?.message || 'Failed to reject user.'));
  };

  const handleDeactivate = (userId) => {
    if (!window.confirm('Deactivate this user? They will lose portal access on next login.')) return;
    axios.put(`${API}/api/admin/access/users/${userId}`, { status: 'inactive' })
      .then(() => loadUsers())
      .catch(e => setError(e?.response?.data?.detail || e?.message || 'Failed to deactivate user.'));
  };

  const handleReactivate = (userId) => {
    axios.put(`${API}/api/admin/access/users/${userId}`, { status: 'active' })
      .then(() => loadUsers())
      .catch(e => setError(e?.response?.data?.detail || e?.message || 'Failed to activate user.'));
  };

  const handleDeleteUser = (userId) => {
    if (!window.confirm('Permanently remove this user record? This cannot be undone.')) return;
    axios.delete(`${API}/api/admin/access/users/${userId}`)
      .then(() => loadUsers())
      .catch(e => setError(e?.response?.data?.detail || e?.message || 'Failed to delete user.'));
  };

  useEffect(() => {
    loadGroups();
    loadPending();
  }, []);

  useEffect(() => {
    if (activeTab === 'users') loadUsers();
    setError('');       // clear cross-tab error state on tab switch
    setUpstreamInfo(''); // clear upstream info on tab switch
  }, [activeTab, statusFilter, search]);

  return {
    activeTab, setActiveTab: switchTab,
    groups, users, pendingUsers, pendingCount,
    loading, search, setSearch, statusFilter, setStatusFilter,
    groupFilter, setGroupFilter, viewGroupMembers,
    groupDialog, setGroupDialog,
    addUserDialog, setAddUserDialog,
    approveDialog, setApproveDialog,
    editUserDialog, setEditUserDialog,
    error, upstreamInfo,
    loadUsers, loadGroups, loadPending,
    handleCreateGroup, handleUpdateGroup, handleDeleteGroup,
    handleAddUser, handleEditUser, handleApprove, handleReject,
    handleDeactivate, handleReactivate, handleDeleteUser,
  };
}

// ── Audit Log Tab ─────────────────────────────────────────────────────────────
function AuditLogTab() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [filters, setFilters] = useState({ action: '', actor: '', target: '' });
  const [upstreamInfo, setUpstreamInfo] = useState('');
  const PER_PAGE = 30;
  const timerRef = useRef(null);

  const load = useCallback((p = 1) => {
    setLoading(true);
    const params = { page: p, per_page: PER_PAGE };
    if (filters.action) params.action = filters.action;
    if (filters.actor)  params.actor  = filters.actor;
    if (filters.target) params.target = filters.target;
    axios.get(`${API}/api/admin/access/audit-log`, { params })
      .then(r => {
        setEntries(r.data.data || []);
        setTotal(r.data.total || 0);
        setLastRefreshed(new Date());
        setPage(p);
        if (r.data._upstream_info) setUpstreamInfo(r.data._upstream_info);
        else setUpstreamInfo('');
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => { load(1); }, [load]);

  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (autoRefresh) {
      timerRef.current = setInterval(() => load(page), 30000);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [autoRefresh, load, page]);

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  const fmtTs = (ts) => {
    if (!ts) return '—';
    return new Date(ts).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  };

  return (
    <div>
      {upstreamInfo && (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', padding: '10px 16px', borderRadius: 8, marginBottom: 14, fontSize: 13, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <Info size={15} color="#f59e0b" style={{ flexShrink: 0, marginTop: 1 }} />
          <span>{upstreamInfo}</span>
        </div>
      )}
      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <select
          value={filters.action}
          onChange={e => setFilters(f => ({ ...f, action: e.target.value }))}
          style={{ ...inputStyle, width: 190, marginBottom: 0 }}
          data-testid="audit-action-filter"
        >
          <option value="">All Actions</option>
          {Object.entries(ACTION_META).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <input
          value={filters.actor}
          onChange={e => setFilters(f => ({ ...f, actor: e.target.value }))}
          placeholder="Filter by admin email…"
          style={{ ...inputStyle, width: 210, marginBottom: 0 }}
          data-testid="audit-actor-filter"
        />
        <input
          value={filters.target}
          onChange={e => setFilters(f => ({ ...f, target: e.target.value }))}
          placeholder="Filter by target…"
          style={{ ...inputStyle, width: 200, marginBottom: 0 }}
          data-testid="audit-target-filter"
        />
        <button onClick={() => load(1)} style={btnSecondary} data-testid="audit-refresh-btn">
          <RefreshCw size={13} style={loading ? { animation: 'spin 0.9s linear infinite' } : {}} />
          Refresh
        </button>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#6b7280', cursor: 'pointer', userSelect: 'none' }}
            onClick={() => setAutoRefresh(v => !v)}>
            <div style={{
              width: 10, height: 10, borderRadius: '50%',
              background: autoRefresh ? '#10b981' : '#d1d5db',
              boxShadow: autoRefresh ? '0 0 0 3px rgba(16,185,129,0.25)' : 'none',
              flexShrink: 0,
              transition: 'all 0.3s',
            }} />
            Auto-refresh (30s)
          </label>
          {lastRefreshed && (
            <span style={{ fontSize: 11, color: '#9ca3af' }}>
              Updated {lastRefreshed.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
          )}
        </div>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        {total > 0 && (
          <div style={{ padding: '8px 16px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', fontSize: 12, color: '#6b7280' }}>
            {total.toLocaleString()} event{total !== 1 ? 's' : ''} total
          </div>
        )}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                {['Timestamp', 'Action', 'Performed By', 'Target', 'Details'].map(h => (
                  <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb', fontSize: 12, whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && entries.length === 0 ? (
                <tr><td colSpan={5} style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading…</td></tr>
              ) : entries.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ padding: '48px 20px', textAlign: 'center', color: '#9ca3af' }}>
                    <ScrollText size={34} style={{ margin: '0 auto 10px', display: 'block', opacity: 0.4 }} />
                    <div style={{ fontSize: 14, fontWeight: 500, color: '#6b7280', marginBottom: 4 }}>No audit events yet</div>
                    <div style={{ fontSize: 12 }}>Actions performed on users and groups will appear here in real-time.</div>
                  </td>
                </tr>
              ) : entries.map(e => (
                <tr key={e.id} style={{ borderBottom: '1px solid #f3f4f6', transition: 'background 0.1s' }}
                  onMouseEnter={ev => ev.currentTarget.style.background = '#fafafa'}
                  onMouseLeave={ev => ev.currentTarget.style.background = ''}>
                  <td style={{ padding: '11px 16px', color: '#6b7280', whiteSpace: 'nowrap', fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
                    {fmtTs(e.ts)}
                  </td>
                  <td style={{ padding: '11px 16px' }}>
                    <ActionBadge action={e.action} />
                  </td>
                  <td style={{ padding: '11px 16px', color: '#374151', fontWeight: 500 }}>
                    {e.performed_by}
                  </td>
                  <td style={{ padding: '11px 16px', color: '#6b7280' }}>
                    {e.target_email || e.target_name || '—'}
                  </td>
                  <td style={{ padding: '11px 16px', color: '#9ca3af', fontSize: 12, maxWidth: 260 }}>
                    {e.details
                      ? (typeof e.details === 'object'
                          ? Object.entries(e.details).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : JSON.stringify(v)}`).join(' · ')
                          : String(e.details))
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ padding: '12px 16px', borderTop: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
            <button
              onClick={() => load(page - 1)}
              disabled={page <= 1}
              style={{ ...btnSmall, background: '#f3f4f6', color: '#374151', opacity: page <= 1 ? 0.4 : 1 }}
            >
              <ChevronLeft size={13} />
            </button>
            <span style={{ fontSize: 13, color: '#6b7280' }}>Page {page} of {totalPages}</span>
            <button
              onClick={() => load(page + 1)}
              disabled={page >= totalPages}
              style={{ ...btnSmall, background: '#f3f4f6', color: '#374151', opacity: page >= totalPages ? 0.4 : 1 }}
            >
              <ChevronRight size={13} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}


// ── Main page (presentation only — no hooks here) ─────────────────────────────
export default function UserAccessManagement() {
  const {
    activeTab, setActiveTab,
    groups, users, pendingUsers, pendingCount,
    loading, search, setSearch, statusFilter, setStatusFilter,
    groupFilter, setGroupFilter, viewGroupMembers,
    groupDialog, setGroupDialog,
    addUserDialog, setAddUserDialog,
    approveDialog, setApproveDialog,
    editUserDialog, setEditUserDialog,
    error, upstreamInfo,
    loadUsers,
    handleCreateGroup, handleUpdateGroup, handleDeleteGroup,
    handleAddUser, handleEditUser, handleApprove, handleReject,
    handleDeactivate, handleReactivate, handleDeleteUser,
  } = useAccessManagement();

  // Client-side filter: when a group is selected from the Groups tab, show only its members.
  const displayedUsers = groupFilter
    ? users.filter(u => u.group_id === groupFilter.id)
    : users;

  const tabs = [
    { id: 'pending',   label: 'Pending Approvals', icon: Clock,       badge: pendingCount > 0 ? pendingCount : null },
    { id: 'users',     label: 'All Users',          icon: Users,       badge: null },
    { id: 'groups',    label: 'Groups',             icon: Shield,      badge: null },
    { id: 'audit-log', label: 'Audit Log',          icon: ScrollText,  badge: null },
  ];
  return (
    <div style={{ padding: 28, fontFamily: '"DM Sans", sans-serif', background: '#F4F3F5', minHeight: '100%' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#091F2C' }}>
          User Access Management
        </h1>
        <p style={{ margin: '6px 0 0', color: '#6b7280', fontSize: 14 }}>
          Manage portal access for Microsoft and TechnoFocus users.
          Only @microsoft.com and @technofocus.co are permitted.
        </p>
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', color: '#b91c1c', padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
          {error}
        </div>
      )}

      {upstreamInfo && !error && (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 13, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <Info size={15} color="#f59e0b" style={{ flexShrink: 0, marginTop: 1 }} />
          <span>{upstreamInfo}</span>
        </div>
      )}

      {/* Tabs */}
      <div style={{
        display: 'flex', gap: 4, marginBottom: 20,
        borderBottom: '2px solid #e5e7eb',
      }}>
        {tabs.map(t => (
          <button
            key={t.id}
            data-testid={`tab-${t.id}`}
            onClick={() => setActiveTab(t.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: 7,
              padding: '10px 18px',
              background: 'none', border: 'none', cursor: 'pointer',
              borderBottom: activeTab === t.id ? '2px solid #194449' : '2px solid transparent',
              marginBottom: -2,
              color: activeTab === t.id ? '#194449' : '#6b7280',
              fontSize: 14, fontWeight: activeTab === t.id ? 700 : 500,
              fontFamily: '"DM Sans", sans-serif',
              transition: 'color 0.15s',
            }}
          >
            <t.icon size={15} />
            {t.label}
            {t.badge && (
              <span style={{
                background: '#ef4444', color: '#fff',
                fontSize: 11, fontWeight: 700, borderRadius: '50%',
                minWidth: 18, height: 18, display: 'inline-flex',
                alignItems: 'center', justifyContent: 'center',
                padding: '0 4px',
              }}>{t.badge}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── PENDING TAB ─────────────────────────────────────────────────── */}
      {activeTab === 'pending' && (
        <div>
          {pendingUsers.length === 0 ? (
            <div style={{
              textAlign: 'center', padding: '60px 20px',
              background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb',
            }}>
              <CheckCircle size={40} color="#10b981" style={{ marginBottom: 12 }} />
              <h3 style={{ margin: 0, color: '#374151', fontSize: 16 }}>No Pending Requests</h3>
              <p style={{ color: '#9ca3af', fontSize: 14, marginTop: 6 }}>
                All access requests have been processed.
              </p>
            </div>
          ) : (
            <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
              {/* Info banner */}
              <div style={{ padding: '12px 20px', background: '#fffbeb', borderBottom: '1px solid #fde68a', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Info size={15} color="#f59e0b" />
                <span style={{ fontSize: 13, color: '#92400e' }}>
                  {pendingCount} user{pendingCount !== 1 ? 's' : ''} awaiting access approval.
                  Approving grants immediate access on next login.
                </span>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    {['Email', 'Domain', 'Requested', 'Actions'].map(h => (
                      <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb', fontSize: 12 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pendingUsers.map(u => (
                    <tr key={u.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '12px 16px', fontWeight: 500, color: '#111827' }}>{u.email}</td>
                      <td style={{ padding: '12px 16px', color: '#6b7280' }}>
                        @{u.domain || u.email?.split('@')[1] || '—'}
                      </td>
                      <td style={{ padding: '12px 16px', color: '#6b7280' }}>
                        {u.first_seen_at ? new Date(u.first_seen_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button
                            data-testid={`approve-btn-${u.id}`}
                            onClick={() => setApproveDialog(u)}
                            style={{ ...btnSmall, background: '#10b981', color: '#fff' }}
                          >
                            <UserCheck size={13} /> Approve
                          </button>
                          <button
                            data-testid={`reject-btn-${u.id}`}
                            onClick={() => handleReject(u.id)}
                            style={{ ...btnSmall, background: '#fef2f2', color: '#ef4444', border: '1px solid #fca5a5' }}
                          >
                            <UserX size={13} /> Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── USERS TAB ───────────────────────────────────────────────────── */}
      {activeTab === 'users' && (
        <div>
          {/* Toolbar */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', flex: 1, maxWidth: 300 }}>
              <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && loadUsers()}
                placeholder="Search by email…"
                data-testid="users-search-input"
                style={{ ...inputStyle, paddingLeft: 32, marginBottom: 0, width: '100%', boxSizing: 'border-box' }}
              />
            </div>
            <select value={statusFilter} onChange={e => { setGroupFilter(null); setStatusFilter(e.target.value); }} style={{ ...inputStyle, marginBottom: 0, width: 140 }}>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="inactive">Inactive</option>
              <option value="all">All</option>
            </select>

            {/* Group drill-down chip — shown when arriving from Groups tab */}
            {groupFilter && (
              <div
                data-testid="group-filter-chip"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: 'rgba(25,68,73,0.08)', border: '1.5px solid rgba(25,68,73,0.25)',
                  borderRadius: 20, padding: '4px 6px 4px 12px',
                }}
              >
                <Building2 size={13} color="#194449" />
                <span style={{ fontSize: 12, fontWeight: 600, color: '#194449' }}>{groupFilter.name}</span>
                <button
                  data-testid="clear-group-filter-btn"
                  onClick={() => setGroupFilter(null)}
                  title="Show all users"
                  style={{
                    background: 'rgba(25,68,73,0.1)', border: 'none', cursor: 'pointer',
                    color: '#194449', width: 18, height: 18, borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 13, fontWeight: 700, lineHeight: 1, flexShrink: 0,
                  }}
                >×</button>
              </div>
            )}

            <button onClick={loadUsers} style={btnSecondary} data-testid="refresh-users-btn">
              <RefreshCw size={13} /> Refresh
            </button>
            <button onClick={() => setAddUserDialog(true)} style={btnPrimary} data-testid="add-user-btn">
              <Plus size={14} /> Add User
            </button>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Loading…</div>
          ) : (
            <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
              {groupFilter && (
                <div style={{ padding: '8px 16px', background: 'rgba(25,68,73,0.05)', borderBottom: '1px solid #e5e7eb', fontSize: 12, color: '#194449', fontWeight: 600 }}>
                  {displayedUsers.length} member{displayedUsers.length !== 1 ? 's' : ''} in <span style={{ color: '#194449' }}>{groupFilter.name}</span>
                </div>
              )}
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    {['Email', 'Group', 'Effective Role', 'Status', 'Last Login', 'Actions'].map(h => (
                      <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb', fontSize: 12 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {displayedUsers.length === 0 ? (
                    <tr><td colSpan={6} style={{ padding: 32, textAlign: 'center', color: '#9ca3af' }}>
                      {groupFilter ? `No members found in "${groupFilter.name}".` : 'No users found.'}
                    </td></tr>
                  ) : displayedUsers.map(u => (
                    <tr key={u.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '12px 16px', fontWeight: 500, color: '#111827' }}>{u.email}</td>
                      <td style={{ padding: '12px 16px', color: '#6b7280' }}>{u.group_name || '—'}</td>
                      <td style={{ padding: '12px 16px' }}><RoleBadge role={u.effective_role || u.group_role || u.role_type || u.group_role_type} /></td>
                      <td style={{ padding: '12px 16px' }}><StatusBadge status={u.status} /></td>
                      <td style={{ padding: '12px 16px', color: '#6b7280' }}>
                        {u.last_login ? new Date(u.last_login).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Never'}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button
                            onClick={() => setEditUserDialog(u)}
                            style={{ ...btnSmall, background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb' }}
                            data-testid={`edit-user-${u.id}`}
                            title="Edit role / group"
                          >
                            <Edit2 size={12} />
                          </button>
                          {u.status === 'active' ? (
                            <button onClick={() => handleDeactivate(u.id)} style={{ ...btnSmall, background: '#fef2f2', color: '#ef4444', border: '1px solid #fca5a5' }} data-testid={`deactivate-btn-${u.id}`}>
                              Deactivate
                            </button>
                          ) : (
                            <button onClick={() => handleReactivate(u.id)} style={{ ...btnSmall, background: '#f0fdf4', color: '#16a34a', border: '1px solid #86efac' }}>
                              Activate
                            </button>
                          )}
                          <button onClick={() => handleDeleteUser(u.id)} style={{ ...btnSmall, background: 'none', color: '#9ca3af', border: '1px solid #e5e7eb' }}>
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── GROUPS TAB ──────────────────────────────────────────────────── */}
      {activeTab === 'groups' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <button onClick={() => setGroupDialog({ mode: 'create' })} style={btnPrimary} data-testid="create-group-btn">
              <Plus size={14} /> Create Group
            </button>
          </div>

          {groups.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb' }}>
              <Shield size={40} color="#9ca3af" style={{ marginBottom: 12 }} />
              <h3 style={{ margin: 0, color: '#374151', fontSize: 16 }}>No Groups Yet</h3>
              <p style={{ color: '#9ca3af', fontSize: 14, marginTop: 6 }}>
                Create groups to manage user permissions efficiently.
              </p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
              {groups.map(g => (
                <div key={g.id} style={{
                  background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb',
                  padding: 20, opacity: g.is_active ? 1 : 0.5,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#111827' }}>{g.group_name}</h3>
                      {g.description && <p style={{ margin: '4px 0 0', fontSize: 12, color: '#9ca3af' }}>{g.description}</p>}
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => setGroupDialog({ mode: 'edit', initial: g })} style={{ ...btnSmall, background: '#f3f4f6', color: '#374151' }} data-testid={`edit-group-${g.id}`}>
                        <Edit2 size={12} />
                      </button>
                      <button onClick={() => handleDeleteGroup(g.id, g.group_name)} style={{ ...btnSmall, background: '#fef2f2', color: '#ef4444' }} data-testid={`delete-group-${g.id}`}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <RoleBadge role={g.group_role} />
                    {g.member_count > 0 ? (
                      <button
                        data-testid={`view-members-${g.id}`}
                        onClick={() => viewGroupMembers(g)}
                        title={`View all members of ${g.group_name}`}
                        style={{
                          fontSize: 12, color: '#194449', background: 'none', border: 'none',
                          cursor: 'pointer', padding: 0, fontFamily: '"DM Sans", sans-serif',
                          fontWeight: 600, textDecoration: 'underline', textDecorationStyle: 'dotted',
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          transition: 'color 0.15s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.color = '#0d6efd'; }}
                        onMouseLeave={e => { e.currentTarget.style.color = '#194449'; }}
                      >
                        <Users size={12} />
                        {g.member_count} member{g.member_count !== 1 ? 's' : ''}
                      </button>
                    ) : (
                      <span style={{ fontSize: 12, color: '#9ca3af' }}>0 members</span>
                    )}
                    {!g.is_active && (
                      <span style={{ fontSize: 11, color: '#ef4444', fontWeight: 600 }}>DEACTIVATED</span>
                    )}
                  </div>

                  {/* PDM: show POID count */}
                  {g.group_role === 'ms_pdm' && g.partner_one_ids?.length > 0 && (
                    <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {g.partner_one_ids.slice(0, 3).map(poid => (
                        <PoidChip key={poid} poid={poid} />
                      ))}
                      {g.partner_one_ids.length > 3 && (
                        <span style={{ fontSize: 11, color: '#9ca3af' }}>+{g.partner_one_ids.length - 3} more</span>
                      )}
                    </div>
                  )}

                  {/* Program Admin: show page/project count */}
                  {g.group_role === 'ms_prog_admin' && g.program_access && (
                    <div style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>
                      {(g.program_access.pages?.length || 0)} pages,{' '}
                      {(g.program_access.projects?.length || 0)} projects assigned
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── AUDIT LOG TAB ─────────────────────────────────────────────── */}
      {activeTab === 'audit-log' && <AuditLogTab />}

      {/* ── Dialogs ────────────────────────────────────────────────────── */}
      {groupDialog && (
        <GroupDialog
          initial={groupDialog.initial || null}
          onSave={groupDialog.mode === 'create' ? handleCreateGroup : handleUpdateGroup}
          onClose={() => setGroupDialog(null)}
        />
      )}
      {addUserDialog && (
        <AddUserDialog groups={groups} onSave={handleAddUser} onClose={() => setAddUserDialog(false)} />
      )}
      {approveDialog && (
        <ApproveDialog
          user={approveDialog}
          groups={groups}
          onSave={(payload) => handleApprove(approveDialog.id, payload)}
          onClose={() => setApproveDialog(null)}
        />
      )}
      {editUserDialog && (
        <EditUserDialog
          user={editUserDialog}
          groups={groups}
          onSave={(payload) => handleEditUser(editUserDialog.id, payload)}
          onClose={() => setEditUserDialog(null)}
        />
      )}
    </div>
  );
}

// ── Shared styles ──────────────────────────────────────────────────────────────
const labelStyle = {
  display: 'block', fontSize: 12, fontWeight: 600,
  color: '#374151', marginBottom: 6,
};

const inputStyle = {
  width: '100%', height: 40, padding: '0 12px',
  border: '1.5px solid #dde3e5', borderRadius: 6,
  fontSize: 13, fontFamily: '"DM Sans", sans-serif',
  background: '#fff', outline: 'none', boxSizing: 'border-box',
  marginBottom: 0, color: '#111827',
};

const btnPrimary = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 18px', background: '#194449', color: '#fff',
  border: 'none', borderRadius: 6, cursor: 'pointer',
  fontSize: 13, fontWeight: 600, fontFamily: '"DM Sans", sans-serif',
};

const btnSecondary = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 14px', background: '#fff', color: '#374151',
  border: '1.5px solid #dde3e5', borderRadius: 6, cursor: 'pointer',
  fontSize: 13, fontWeight: 500, fontFamily: '"DM Sans", sans-serif',
};

const btnSmall = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '5px 10px', borderRadius: 6, cursor: 'pointer',
  fontSize: 12, fontWeight: 600, fontFamily: '"DM Sans", sans-serif',
  border: 'none',
};
